import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";
import { Reveal, Marquee } from "@/components/Motion";
import { MENU, CATEGORIES } from "@/data/site";

export default function Menu() {
  const [active, setActive] = useState("All");
  const items = active === "All" ? MENU : MENU.filter((m) => m.category === active);

  return (
    <div data-testid="menu-page" className="pt-28">
      <section className="mx-auto max-w-7xl px-5 py-16 md:py-20">
        <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Our Menu</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl font-bold uppercase tracking-tight text-koreyaah-indigo leading-[0.9]">
            Get your <span className="text-koreyaah-red">Seoul food</span> fix
          </h1>
        </Reveal>
        <Reveal delay={0.1}><p className="mt-6 max-w-2xl text-lg text-koreyaah-indigo/80">Bold flavours, quick bites, and the joy of sharing. Every dish is crafted with authentic ingredients and a modern twist.</p></Reveal>
      </section>

      <div className="border-y-2 border-koreyaah-indigo bg-koreyaah-cobalt py-3 text-koreyaah-cream">
        <Marquee reverse className="font-display text-xl md:text-2xl font-semibold uppercase tracking-wide" items={["Buldak Raboki", "K-Wings", "Corn Dogs", "Bao Buns", "Dumplings", "Taro", "Matcha", "Karaage Fries"]} />
      </div>

      {/* Sticky category filters */}
      <div className="sticky top-16 z-30 border-b border-koreyaah-indigo/10 bg-koreyaah-cream/90 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-5 py-4">
          <div className="flex gap-2.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`menu-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                onClick={() => setActive(c)}
                className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors duration-200 ${
                  active === c ? "bg-koreyaah-red text-white" : "border-2 border-koreyaah-indigo/20 text-koreyaah-indigo hover:border-koreyaah-red hover:text-koreyaah-red"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="mx-auto max-w-7xl px-5 py-12">
        <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {items.map((m, i) => (
              <motion.div
                layout
                key={m.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.04 }}
                data-testid={`menu-item-${i}`}
                className="group flex h-full flex-col rounded-[2rem] bg-white border-2 border-koreyaah-indigo/10 p-5 transition-shadow duration-300 hover:shadow-pop"
              >
                <div className="relative overflow-hidden rounded-[1.5rem]">
                  <img src={m.img} alt={m.name} loading="lazy" className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  {m.tag && <span className="absolute left-3 top-3 rounded-full bg-koreyaah-red px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">{m.tag}</span>}
                </div>
                <div className="mt-5 flex flex-1 flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-semibold uppercase tracking-widest text-koreyaah-red">{m.category}</span>
                    {m.spice > 0 && (
                      <span className="flex items-center gap-0.5 text-koreyaah-red" title={`Spice level ${m.spice} of 5`} data-testid={`menu-item-${i}-spice`}>
                        {Array.from({ length: m.spice }).map((_, s) => <Flame key={s} size={13} fill="currentColor" />)}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-1 font-display text-2xl font-semibold text-koreyaah-indigo">
                    {m.name}
                    {m.serves && <span className="ml-2 align-middle font-body text-xs font-medium normal-case text-koreyaah-indigo/50">{m.serves}</span>}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-koreyaah-indigo/70">{m.desc}</p>
                  {m.note && <p className="mt-1 text-xs italic text-koreyaah-indigo/50">{m.note}</p>}
                  <div className="mt-auto flex flex-wrap gap-2 pt-4" data-testid={`menu-item-${i}-prices`}>
                    {m.variants.map((v, vi) => (
                      <span key={vi} className="inline-flex items-baseline gap-1.5 rounded-full bg-koreyaah-indigo/5 px-3 py-1 text-sm font-bold text-koreyaah-indigo">
                        {v.label && <span className="text-xs font-semibold uppercase tracking-wide text-koreyaah-indigo/60">{v.label}</span>}
                        <span className="text-koreyaah-red">₹{v.price}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section className="mx-auto max-w-4xl px-5 pb-24 text-center">
        <Reveal>
          <div className="rounded-[2.5rem] bg-koreyaah-cobalt p-12 text-koreyaah-cream">
            <h2 className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight">Hungry yet?</h2>
            <p className="mx-auto mt-4 max-w-md text-koreyaah-cream/80">Come find your K-Spot in Sarjapur, Bangalore.</p>
            <Link to="/contact" data-testid="menu-cta-contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-koreyaah-red px-8 py-4 text-sm font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:-translate-y-1">Visit Us <ArrowRight size={18} /></Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
